import React, { Dispatch, SetStateAction } from 'react';
import { Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import FormActions from '../../../components/FormActions';
import { translate } from '../../../services/translate';
import MyAppText from '../../../components/MyAppText';
import PauseReasonsDropdown from '../../../components/app-agent/PauseReasonsDropdown';
import { appAgentService } from '../../../services/central-api/app-agent';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    pauseReason: number;
    setPauseReason: Dispatch<SetStateAction<number>>;
    onClose: () => void;
    unit: AppAgentUnit;
}

export default function OperationalPauseModal({ isModalVisible, setModalVisible, pauseReason, setPauseReason, unit, onClose }: ModalProps) {
    const { styles } = useStyles(styleSheet);

    async function pauseUnit() {
        try {
            if (unit) {
                await appAgentService.pauseUnpauseUnit(unit.id, unit.paused ? 'unpause' : 'pause', pauseReason);
                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });
                setPauseReason(0);
                setModalVisible(false);
            }
            onClose();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                setModalVisible(false);
            }}
        >
            <View style={styles.container}>
                <View style={styles.formContainer}>
                    <View style={{ flexDirection: 'column', rowGap: 8 }}>
                        <MyAppText style={styles.header}>{translate('unitOperationalPause')}</MyAppText>
                        <MyAppText style={styles.subheader}>{translate('unitOperationalPauseDescription')}</MyAppText>
                    </View>
                    <PauseReasonsDropdown
                        setValue={setPauseReason}
                        value={pauseReason}
                        zIndex={2}
                        unitId={unit.id}
                    />
                    <FormActions
                        buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9 }}
                        onSubmit={async () => {
                            setModalVisible(false);
                            await pauseUnit();
                        }}
                        onClose={() => {
                            onClose();
                            setModalVisible(false);
                            setPauseReason(0);
                        }}
                        confirmText='pause'
                        disabled={!pauseReason}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: {
        minWidth: 300,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32
    },
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 14,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    inputLabelStyle: {
        fontSize: 14,
        fontWeight: '600',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
        marginBottom: 8,
    },
}));
